import { keycloak } from "@auth/keycloak";
import {
  setAccessToken,
  refreshAuthToken,
  initializeTokenManager,
  performLogout,
  redirectToPlatformLogin,
  getAccessToken
} from "@auth/tokenManager";
import type { KeycloakEventType } from "@auth/keycloakContext";
import { logKeycloakToken, tokenDebugInfo } from "@auth/authDebugLog";

/** check-sso 숨은 iframe 실패(timeout 등). 설정 미완료 시 흔하며 Platform 이동 대상이 아님. */
function isCheckSsoIframeError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /timeout|3rd party|third party|check iframe/i.test(message);
}

/**
 * KeycloakProvider onEvent 핸들러.
 * keycloak-js 콜백(onAuthSuccess/onTokens)으로 받은 access token 을 메모리에 동기화하고,
 * 만료/갱신 실패/로그아웃 등의 이벤트를 처리한다.
 */
export const onKeycloakEvent = (
  event: KeycloakEventType,
  error?: unknown
): void => {
  switch (event) {
    case "onReady":
      logKeycloakToken("onReady", {
        authenticated: keycloak.authenticated,
        ...tokenDebugInfo(keycloak.token),
        memoryToken: tokenDebugInfo(getAccessToken())
      });
      break;

    case "onAuthSuccess":
      logKeycloakToken("onAuthSuccess(before setAccessToken)", {
        ...tokenDebugInfo(keycloak.token)
      });
      if (keycloak.token) setAccessToken(keycloak.token, "onAuthSuccess");
      logKeycloakToken("onAuthSuccess(after setAccessToken)", {
        memoryToken: tokenDebugInfo(getAccessToken())
      });
      initializeTokenManager();
      break;

    case "onAuthLogout":
      // SSO 세션 종료(만료/관리자 취소/다른 탭 로그아웃) → 전체 로그아웃.
      performLogout();
      break;

    case "onAuthError":
      logKeycloakToken("onAuthError", {
        error: error instanceof Error ? error.message : String(error ?? "")
      });
      if (isCheckSsoIframeError(error)) {
        // iframe timeout = SSO 세션 없음/3rd-party 쿠키 차단. 미인증으로 앱 진행(Platform 루프 방지).
        // eslint-disable-next-line no-console
        console.warn(
          "[keycloak:token] check-sso iframe 실패 — Platform 리다이렉트 생략. realm/redirect URI/3p-cookies(step1.html) 및 브라우저 서드파티 쿠키를 확인하세요."
        );
        break;
      }
      // realm/client 설정 오류 등 복구 불가 init 실패만 Platform 으로.
      redirectToPlatformLogin();
      break;

    case "onAuthRefreshError":
      // 즉시 로그아웃하지 않음 — SSO 쿠키가 아직 유효할 수 있음. 다음 요청에서 재시도.
      break;

    case "onTokenExpired":
      // 선제 갱신이 놓친 경우(백그라운드 탭 등) 복구 시도. -1 = 무조건 갱신.
      refreshAuthToken(-1).catch(() => performLogout());
      break;

    default:
      break;
  }
};

/** KeycloakProvider onTokens 핸들러. 콜백/갱신 토큰을 메모리에 반영. */
export const onKeycloakTokens = (tokens: { token?: string }): void => {
  logKeycloakToken("onTokens(before setAccessToken)", {
    ...tokenDebugInfo(tokens.token)
  });
  if (tokens.token) setAccessToken(tokens.token, "onTokens");
  logKeycloakToken("onTokens(after setAccessToken)", {
    memoryToken: tokenDebugInfo(getAccessToken())
  });
};
